import React, { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Weidner Via6 FloorplansList override.
 *
 * Three bedroom-category cards:
 *
 *   1. Outer container: theme-primary background with a thin (1.5 px)
 *      white border and square corners (no border-radius), ~26 px
 *      padding so the inner thumbnail floats with generous breathing
 *      room inside the colored frame. Replaces the original light-gray
 *      (#ECECEC) pad with the brand primary surface so the cards read
 *      as bold colored chips against the primary row background.
 *   2. Inner thumbnail panel: white background, 240 px tall on desktop
 *      / 210 px on phones, holds the slot-group image centered with
 *      object-cover. Pulls double duty as the editable image target
 *      via <EditableSlotGroup>.
 *   3. Body block (centered, below thumb): big bold serif title
 *      "1 Bedroom" in text-on-primary, then a single combined stats
 *      line that pairs the bed count ("1 BED") with the sqft range
 *      ("513-881 sq.ft"). The old uppercase "FLOOR PLAN OPTIONS"
 *      subhead and the title divider stripe have both been removed.
 *   4. Hover state: an iris-expanding primary-tinted overlay covers the
 *      whole card via clip-path inset() animation, and a centered CTA
 *      pill fades + slides in on top. Mirrors the original iris pattern
 *      this component had before the via6 restyle — just applied to the
 *      full card surface instead of a square photo tile.
 *
 * The "View All" card has been removed at the user's request; only the
 * first three bedroom-category groups are shown. Slot bindings (3 card
 * images) and the floorplans collection binding are preserved so the
 * editor surfaces the same controls and the schema linter still passes.
 */

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  /** Just the bed-count display value ("Studio", "1", "2", …) — pairs
   *  with the inline <BedIcon /> in the stats line. */
  bedLabel: string;
  /** Bath count or range ("1", "1-2", "1.5") sourced from
   *  floorplan_baths_min across the plans in this category. Empty
   *  string when no bath data is available so the caller can omit. */
  bathRange: string;
  param: string;
  count: number;
  priceRange: string;
  sqftRange: string;
  planCount: number;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

/**
 * Maximum number of bedroom-category cards we render. Locked at 3 per
 * the latest design — keep this in sync with the slot-group count below
 * (card_image_0..card_image_2) and the JSX category render blocks.
 */
const MAX_CATEGORY_CARDS = 3;

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'STUDIO',
    one: '1 BED',
    two: '2 BED',
    three: '3 BED',
    four: '4 BED',
    five: '5 BED',
  };
  return labels[key] || `${key} BED`;
}

/**
 * Short numeric label that sits next to the bed icon. Studios display
 * "1" (per via6 convention — a studio still has one sleeping area)
 * rather than the word "Studio"; everything else shows the integer
 * bedroom count.
 */
function keyToBedLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: '1',
    one: '1',
    two: '2',
    three: '3',
    four: '4',
    five: '5',
  };
  return labels[key] || key;
}

/**
 * Mirrors via6's bath count next to the bathtub icon. Pulls from
 * floorplan_baths_min across plans in the category; formats whole
 * numbers as "1" and decimals as "1.5". Returns a range ("1-2") when
 * plans differ, or '' when no bath data is present.
 */
function formatBathRange(plans: Floorplan[]): string {
  const baths = plans
    .map((p) => (p.floorplan_baths_min ? parseFloat(String(p.floorplan_baths_min)) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!baths.length) return '';
  const fmt = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(1));
  const min = Math.min(...baths);
  const max = Math.max(...baths);
  if (min === max) return fmt(min);
  return `${fmt(min)}-${fmt(max)}`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

/**
 * Mirrors via6's "513-881 sq.ft" stat line. Returns '' when no plans in
 * the group have a sqft figure, so the caller can simply omit the line.
 */
function formatSqftRange(plans: Floorplan[]): string {
  const sqfts = plans
    .flatMap((p) => [p.floorplan_sqft_min, p.floorplan_sqft_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!sqfts.length) return '';
  const min = Math.min(...sqfts);
  const max = Math.max(...sqfts);
  if (min === max) return `${min.toLocaleString()} sq.ft`;
  return `${min.toLocaleString()}-${max.toLocaleString()} sq.ft`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      bedLabel: keyToBedLabel(key),
      bathRange: formatBathRange(plans),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
      sqftRange: formatSqftRange(plans),
      planCount: plans.length,
    };
  });
}

// ============================================================================
// Inline stat icons (bed + bath)
// ============================================================================

/**
 * Simple line-art bed silhouette — square mattress base + two pillows
 * along the top edge. Stroke uses currentColor so it inherits the
 * text-on-primary color from the parent stats row. Sized via CSS in
 * `.fp-card-stat-icon` (16px square by default).
 */
function BedIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* base / frame */}
      <path d="M2 18v-5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v5" />
      <path d="M2 20h20" />
      <path d="M22 18H2" />
      {/* pillows */}
      <path d="M6 10V8a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
      <path d="M13 10V8a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

/**
 * Simple line-art bathtub silhouette with a small shower head/faucet
 * over the rim. Stroke inherits color the same way as BedIcon.
 */
function BathIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* tub body */}
      <path d="M3 13h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-3z" />
      {/* drip legs */}
      <path d="M6 20l-1 2" />
      <path d="M18 20l1 2" />
      {/* faucet arm + spout */}
      <path d="M5 13V6a2 2 0 0 1 2-2h2" />
      <circle cx="11" cy="6" r="1.6" />
    </svg>
  );
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function pickInteriorPhotoIndices(gallery: PhotoGallery[], count: number): number[] {
  if (!gallery.length) return Array.from({ length: count }, (_, i) => i);

  const seen = new Set<string>();
  const scored: { originalIndex: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ originalIndex: i, score: scoreInterior(photo) });
  }

  scored.sort((a, b) => b.score - a.score);

  const pool = scored.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    const indices = pool.map((p) => p.originalIndex);
    while (indices.length < count) {
      indices.push(indices.length < gallery.length ? indices.length : 0);
    }
    return indices;
  }

  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].originalIndex);
  }
  return picked;
}

function useSmartInteriorIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const indices = pickInteriorPhotoIndices(gallery, MAX_CATEGORY_CARDS);
    return indices as [number, number, number];
  }, [gallery]);
}

// ============================================================================
// Slot group helpers for overridable card images
// ============================================================================

function makeCardImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const cardImage0Def = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage1Def = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage2Def = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

/**
 * Full-width "View All Floor Plans" CTA that sits beneath the three
 * bedroom-category cards. Editable via the same slot-group pattern
 * PropertyOverview uses for its Apply/Gallery CTAs — defaults route to
 * /floor-plans with a sensible label.
 */
const viewAllCtaDef = {
  label: 'View All Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'View All Floorplans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Card primitives
// ============================================================================

function ThumbImage({ img, fallbackAlt }: { img: { image?: string; alt?: string }; fallbackAlt: string }) {
  return img.image ? (
    <VibesitesImage
      url={img.image}
      alt={img.alt || fallbackAlt}
      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
    />
  ) : (
    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
  );
}

/**
 * Overlay that sits on top of the entire card. The `.fp-card-overlay`
 * panel uses clip-path: inset(50% …) → inset(0%) to iris-expand from
 * the center on hover; the CTA pill fades + slides in once the overlay
 * has nearly finished spreading.
 */
function HoverOverlay({ ctaLabel = 'View Plans' }: { ctaLabel?: string }) {
  return (
    <div className="fp-card-overlay" aria-hidden="true">
      <span className="fp-card-cta-hover">
        <span className="fp-card-cta-hover-label">{ctaLabel}</span>
      </span>
    </div>
  );
}

interface CategoryCardProps {
  cat: BedroomCategory;
  children: React.ReactNode;
}

/**
 * Renders one via6-style bedroom-category card with an iris-expand hover
 * overlay. The <a> wraps the whole tile so clicking anywhere routes to
 * /floor-plans?bedroom=<key>. The inner white thumbnail panel is the
 * editable image target (the caller passes the <EditableSlotGroup> as
 * `children`).
 */
function CategoryCard({ cat, children }: CategoryCardProps) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card group block fp-card-item"
    >
      <div className="fp-card-thumb relative overflow-hidden">{children}</div>
      <div className="fp-card-body">
        <h3 className="fp-card-title font-heading">{cat.label}</h3>
        <div className="fp-card-stats">
          <span className="fp-card-stat">
            <BedIcon className="fp-card-stat-icon" />
            <span className="fp-card-stat-value">{cat.bedLabel}</span>
          </span>
          {cat.bathRange ? (
            <span className="fp-card-stat">
              <BathIcon className="fp-card-stat-icon" />
              <span className="fp-card-stat-value">{cat.bathRange}</span>
            </span>
          ) : null}
          {cat.sqftRange ? (
            <span className="fp-card-stat fp-card-sqft">
              <span className="fp-card-stat-value">{cat.sqftRange}</span>
            </span>
          ) : null}
        </div>
      </div>
      <HoverOverlay ctaLabel="View Plans" />
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () =>
      floorplans?.length
        ? groupFloorplansByBeds(floorplans).slice(0, MAX_CATEGORY_CARDS)
        : [],
    [floorplans],
  );

  const [idx0, idx1, idx2] = useSmartInteriorIndices();

  const def0 = useMemo(() => makeCardImageSlotDef('Card 1 Image', idx0), [idx0]);
  const def1 = useMemo(() => makeCardImageSlotDef('Card 2 Image', idx1), [idx1]);
  const def2 = useMemo(() => makeCardImageSlotDef('Card 3 Image', idx2), [idx2]);

  const img0 = useSlotGroup('floorplanslist.card_image_0', def0);
  const img1 = useSlotGroup('floorplanslist.card_image_1', def1);
  const img2 = useSlotGroup('floorplanslist.card_image_2', def2);

  const viewAllCta = useSlotGroup('floorplanslist.view_all_cta', viewAllCtaDef);

  const catSlotImages = [img0, img1, img2];
  const catSlotDefs = [def0, def1, def2];

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  // Lazy-load the "Satisfy" cursive Google Font for the .vibe-cursive-accent
  // span in the section title. De-duped against PropertyOverview /
  // Header by the data-vibe-font marker.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  const hasCategories = categories.length > 0;

  return (
    <section className="pt-12 md:pt-16 pb-8 md:pb-12 bg-[var(--vibe-color-primary)]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="floorplanslist.title"
          as="h2"
          className="font-heading text-center text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight uppercase mb-8 md:mb-12 [&_strong]:font-normal [&_b]:font-normal"
        />
        {hasCategories ? (
          <>
            <div ref={gridRef} className="fp-grid">
              {/* Three bedroom-category cards — each uses a literal groupPath for static linter detection */}
              {categories.length > 0 && (
                <CategoryCard cat={categories[0]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_0" definition={catSlotDefs[0]} className="absolute inset-0">
                    <ThumbImage img={catSlotImages[0]} fallbackAlt={`${categories[0].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 1 && (
                <CategoryCard cat={categories[1]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_1" definition={catSlotDefs[1]} className="absolute inset-0">
                    <ThumbImage img={catSlotImages[1]} fallbackAlt={`${categories[1].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 2 && (
                <CategoryCard cat={categories[2]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_2" definition={catSlotDefs[2]} className="absolute inset-0">
                    <ThumbImage img={catSlotImages[2]} fallbackAlt={`${categories[2].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
            </div>
            <div className="fp-view-all-wrap">
              <EditableSlotGroup
                groupPath="floorplanslist.view_all_cta"
                definition={viewAllCtaDef}
                className="block w-full"
              >
                <a
                  href={viewAllCta.url || '/floor-plans'}
                  target={viewAllCta._target || '_self'}
                  className="fp-view-all-cta"
                >
                  <span className="fp-view-all-cta-label">{viewAllCta.label || 'View All Floorplans'}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Cursive accent for the title's <span class="vibe-cursive-accent">
           — Satisfy is lazy-loaded in the useEffect above. text-transform:
           none escapes the heading's uppercase utility so the script glyphs
           render in their natural mixed case. */
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }

        /* Grid — three across on desktop, stacks to two on tablet and
           one on phones. */
        .fp-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        @media (max-width: 1023px) {
          .fp-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.625rem; }
        }
        @media (max-width: 600px) {
          .fp-grid { grid-template-columns: 1fr; }
        }

        /* Outer card surround — primary-color pad with a crisp white
           border that pops the tile off the page background. position:
           relative so the absolute hover overlay lays over the entire
           card. overflow: hidden so the overlay's clip-path stays inside
           the rounded corners. */
        .fp-card {
          position: relative;
          background-color: var(--vibe-color-primary);
          border: 1.5px solid #FFFFFF;
          border-radius: 0;
          padding: 26px;
          text-decoration: none;
          color: var(--vibe-color-text-on-primary);
          overflow: hidden;
        }
        /* Lighter inner pad — sits inset from the white border so the
           original primary color still shows as a thin frame between the
           border and this lighter surface. Behind the thumb and body. */
        .fp-card::before {
          content: "";
          position: absolute;
          inset: 12px;
          background-color: color-mix(in srgb, var(--vibe-color-primary) 95%, #FFFFFF);
          z-index: 0;
          pointer-events: none;
        }

        /* Inner white thumbnail panel — fixed height so every card
           lines up; image fills it with object-cover. Square corners to
           match the now-radiusless outer card. The card's outer padding
           (.fp-card) supplies the breathing room around this panel. */
        .fp-card-thumb {
          position: relative;
          z-index: 1;
          background-color: #FFFFFF;
          border-radius: 0;
          height: 240px;
          overflow: hidden;
        }
        @media (max-width: 767px) {
          .fp-card-thumb { height: 210px; }
        }

        /* Body sits below the thumb. position: relative + z-index puts
           it above the .fp-card::before lighter pad. */
        .fp-card-body {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 18px 12px 6px;
        }

        .fp-card-title {
          font-size: clamp(22px, 2.2vw, 28px);
          font-weight: 400;
          line-height: 1.15;
          color: var(--vibe-color-text-on-primary);
          margin: 0 0 10px;
          text-transform: none;
        }

        /* Combined stats line — bed icon + count, bath icon + count,
           and sqft range on the same row, separated by thin vertical
           rules between siblings. Uses a centered inline-flex layout
           so the row stays visually balanced as items show/hide
           depending on which data the floor plans expose. */
        .fp-card-stats {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.18em;
          color: var(--vibe-color-text-on-primary);
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        /* One bed/bath/sqft chip: icon + value, with the icon sized via
           .fp-card-stat-icon and currentColor stroke so it inherits the
           on-primary text color. */
        .fp-card-stat {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          line-height: 1;
        }

        /* Generic divider between adjacent stat chips — works for any
           combination of bed / bath / sqft visibility. */
        .fp-card-stat + .fp-card-stat {
          padding-left: 10px;
          border-left: 1px solid var(--vibe-color-secondary);
        }

        .fp-card-stat-icon {
          width: 16px;
          height: 16px;
          flex: 0 0 auto;
          display: block;
        }

        .fp-card-stat-value {
          display: inline-block;
          letter-spacing: 0.18em;
        }

        /* ----- Full-width "View All Floorplans" CTA ----- */

        /* Wrapper matches the .fp-grid max-width so the CTA's full width
           aligns flush with the card row above it. Top margin separates
           it visually from the cards. */
        .fp-view-all-wrap {
          max-width: 1100px;
          margin: 1.5rem auto 0;
        }
        @media (max-width: 1023px) {
          .fp-view-all-wrap { margin-top: 1.25rem; }
        }

        /* The CTA itself — block-level so it spans the full container
           width, centered text, pill-shaped (fully rounded) with the
           theme secondary as the fill. Label uses text-on-primary so
           the wordmark matches the section's other on-primary copy
           (title, card stats). Hover inverts to a white fill with
           secondary-color text. */
        .fp-view-all-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 1rem 1.5rem;
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          border: 1.5px solid var(--vibe-color-secondary);
          border-radius: 9999px;
          font-family: var(--vibe-font-body, inherit);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.2em;
          line-height: 1;
          text-transform: uppercase;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.3s ease, color 0.3s ease,
                      border-color 0.3s ease, transform 0.2s ease;
        }
        .fp-view-all-cta:hover,
        .fp-view-all-cta:focus-visible {
          background-color: #FFFFFF;
          color: var(--vibe-color-secondary);
          border-color: #FFFFFF;
        }
        .fp-view-all-cta-label {
          display: inline-block;
        }

        /* ----- Iris-expanding hover overlay ----- */

        /* The overlay panel covers the entire card. clip-path: inset()
           starts as a 0×0 point at the center and animates to inset(0%)
           (full card) on parent :hover, producing the iris square-shaped
           reveal. Semi-transparent primary (~65%) so the underlying
           thumbnail + body remain visible through the tint as the iris
           spreads. z-index above the body but below the EditableSlotGroup
           wrapper in edit mode (pointer events are disabled, see
           [data-vibesite-edit-mode] block). */
        .fp-card-overlay {
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, var(--vibe-color-primary) 65%, transparent);
          clip-path: inset(50% 50% 50% 50%);
          -webkit-clip-path: inset(50% 50% 50% 50%);
          transition: clip-path 0.55s cubic-bezier(0.65, 0, 0.35, 1),
                      -webkit-clip-path 0.55s cubic-bezier(0.65, 0, 0.35, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 5;
          pointer-events: none;
        }
        .fp-card:hover .fp-card-overlay,
        .fp-card:focus-visible .fp-card-overlay {
          clip-path: inset(0% 0% 0% 0%);
          -webkit-clip-path: inset(0% 0% 0% 0%);
        }

        /* CTA pill — fades + slides in after the iris has had time to
           spread, so it lands on a fully-painted card. Transparent body
           with a thin on-primary border matches the Hero CTA language. */
        .fp-card-cta-hover {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 1.75rem;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary) 75%, transparent);
          border-radius: 9999px;
          background: transparent;
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body, inherit);
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: 1;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          white-space: nowrap;
          opacity: 0;
          transform: translateY(10px);
          pointer-events: auto;
          transition: opacity 0.3s ease 0.35s,
                      transform 0.35s cubic-bezier(0.22, 1, 0.36, 1) 0.35s,
                      border-color 0.3s ease,
                      background-color 0.3s ease,
                      color 0.3s ease;
        }
        .fp-card:hover .fp-card-cta-hover,
        .fp-card:focus-visible .fp-card-cta-hover {
          opacity: 1;
          transform: translateY(0);
          border-color: var(--vibe-color-text-on-primary);
        }
        .fp-card .fp-card-cta-hover:hover,
        .fp-card .fp-card-cta-hover:focus-visible {
          background-color: #FFFFFF;
          color: var(--vibe-color-text-on-secondary);
          border-color: #FFFFFF;
        }
        .fp-card-cta-hover-label {
          position: relative;
          z-index: 1;
        }

        /* In edit mode, the overlay must pass pointer events through so
           the EditableSlotGroup inside the thumbnail still receives
           hover/click for individual image editing. The parent <a>'s
           :hover CSS still fires (it tracks cursor position, not event
           delivery), so the hover transitions keep working. */
        [data-vibesite-edit-mode="true"] .fp-card-overlay {
          pointer-events: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .fp-card,
          .fp-card-overlay,
          .fp-card-cta-hover,
          .fp-card-thumb img {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Find your <span class="vibe-cursive-accent">restful</span> residence at <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>',
    },
  },
  slotGroups: {
    'floorplanslist.card_image_0': cardImage0Def,
    'floorplanslist.card_image_1': cardImage1Def,
    'floorplanslist.card_image_2': cardImage2Def,
    'floorplanslist.view_all_cta': viewAllCtaDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
